import type { CanvasNode } from '@/types/visual-editor';

/**
 * node-placement.ts — 画布节点放置策略
 *
 * 所有新节点统一以「宫格块 (Grid Block)」形式放置：
 * - 每排固定 4 列，节点间隙紧凑 (16px)
 * - Block 级碰撞检测：整块移动，不会与已有节点重叠
 * - 单张生成 = 1×1 宫格，多张 = N 列 × M 行
 *
 * 保留旧 API (getNextNodePosition / getTreeLayoutPositions) 做向后兼容，
 * 内部全部委托给 getGridBlockPositions。
 */

// ── 常量 ──

/** 宫格内节点间隙（水平 & 垂直） */
const CELL_GAP = 16;
/** 节点统一宽度（画布渲染用，与 ClipNode 对齐） */
const NODE_W = 160;
/** 节点默认高度（碰撞检测用；实际高度由 whRatio 决定，此处取保守值） */
const NODE_H = 200;
/** Block 之间的安全间距 */
const BLOCK_GAP = 60;
/** 宫格默认列数 */
const GRID_COLS = 4;
/** 无节点时的默认起始位置 */
const DEFAULT_ORIGIN = { x: 400, y: 100 };

// ── 旧常量（向后兼容老调用方碰撞检测） ──
const GAP_X = 200;
const GAP_Y = 200;
const COLLISION_RADIUS_X = 170;
const COLLISION_RADIUS_Y = 180;

// ── 类型 ──

export interface Position {
  x: number;
  y: number;
}

export interface NodeLike {
  position: Position;
  width?: number;
  height?: number;
}

export interface CanvasViewportLike {
  zoom: number;
  panX: number;
  panY: number;
}

export interface ViewportFrame {
  width: number;
  height: number;
  left?: number;
  top?: number;
}

export interface PlacementExclusionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface GridBlockOptions {
  /** 生成的节点数量 */
  count: number;
  /** 画布上所有现有节点（Block 级碰撞检测） */
  existingNodes: NodeLike[];
  /** 源节点（有则在其下方放置 Block） */
  sourceNode?: NodeLike | null;
  /** 当前视口中心或显式插入锚点（无 source 时优先就近落位） */
  preferredOrigin?: Position | null;
  /** 强制列数（默认 4） */
  cols?: number;
  /** 新生成节点的估算宽度 */
  itemWidth?: number;
  /** 新生成节点的估算高度 */
  itemHeight?: number;
  /** 自动保留的禁入区域，例如 workflow area */
  excludedRects?: PlacementExclusionRect[];
}

export interface GroupedPlacementOptions {
  /** 画布上所有现有节点 */
  existingNodes: NodeLike[];
  /** 同一波生成里已经落位的兄弟节点 */
  siblingNodes: NodeLike[];
  /** 源节点（有则优先围绕源节点布局） */
  sourceNode?: NodeLike | null;
  /** 当前视口中心或显式插入锚点（无 source 时优先就近落位） */
  preferredOrigin?: Position | null;
  /** 强制列数（默认 4） */
  cols?: number;
  /** 新生成节点的估算宽度 */
  itemWidth?: number;
  /** 新生成节点的估算高度 */
  itemHeight?: number;
  /** 自动保留的禁入区域，例如 workflow area */
  excludedRects?: PlacementExclusionRect[];
}

// ── 向后兼容的旧类型 ──

interface PlacementOptions {
  sourceNode?: NodeLike | null;
  direction?: 'right' | 'below';
  existingNodes: NodeLike[];
  preferredOrigin?: Position | null;
  batchIndex?: number;
  batchTotal?: number;
  batchColumnGap?: number;
  itemWidth?: number;
  itemHeight?: number;
  excludedRects?: PlacementExclusionRect[];
}

function getCanvasNodeRecentActivityValue(
  node: Pick<CanvasNode, 'lastActivityAt' | 'updatedAt' | 'enteredCanvasAt' | 'createdAt'>,
): number | null {
  const raw = node.lastActivityAt || node.updatedAt || node.enteredCanvasAt || node.createdAt;
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getRecentActivityClusterNodes(nodes: CanvasNode[]): CanvasNode[] {
  const eligibleNodes = nodes.filter((node) => (
    !!node.position
    && !node.groupChildIds?.length
    && !node.groupId
  ));
  if (eligibleNodes.length === 0) {
    return [];
  }

  let latestActivityValue: number | null = null;
  for (const node of eligibleNodes) {
    const activityValue = getCanvasNodeRecentActivityValue(node);
    if (activityValue === null) continue;
    if (latestActivityValue === null || activityValue > latestActivityValue) {
      latestActivityValue = activityValue;
    }
  }

  if (latestActivityValue === null) {
    return [];
  }

  return eligibleNodes.filter(
    (node) => getCanvasNodeRecentActivityValue(node) === latestActivityValue,
  );
}

export function getRecentActivityClusterOrigin(nodes: CanvasNode[]): Position | null {
  const recentClusterNodes = getRecentActivityClusterNodes(nodes);
  if (recentClusterNodes.length === 0) {
    return null;
  }

  const bounds = recentClusterNodes.reduce((acc, node) => ({
    minX: Math.min(acc.minX, node.position.x),
    maxX: Math.max(acc.maxX, node.position.x),
    minY: Math.min(acc.minY, node.position.y),
    maxY: Math.max(acc.maxY, node.position.y),
  }), {
    minX: Infinity,
    maxX: -Infinity,
    minY: Infinity,
    maxY: -Infinity,
  });

  return {
    x: Math.round((bounds.minX + bounds.maxX) / 2),
    y: Math.round((bounds.minY + bounds.maxY) / 2),
  };
}

export function getViewportCenterPosition(
  viewport: CanvasViewportLike,
  frame: ViewportFrame,
): Position {
  const zoom = Number.isFinite(viewport.zoom) && viewport.zoom > 0 ? viewport.zoom : 1;
  const left = frame.left ?? 0;
  const top = frame.top ?? 0;

  return {
    x: (left + frame.width / 2 - viewport.panX) / zoom,
    y: (top + frame.height / 2 - viewport.panY) / zoom,
  };
}

// ══════════════════════════════════════════
// ★ 核心函数：宫格块布局
// ══════════════════════════════════════════

/**
 * 计算一组节点的宫格布局位置
 *
 * 1. 确定 Block 起点（源节点最近空位 / 空闲区域）
 * 2. 按 4 列宫格排列
 * 3. Block 级碰撞检测 — 尝试四个方向，选最近的
 */
export function getGridBlockPositions(opts: GridBlockOptions): Position[] {
  const {
    count,
    existingNodes,
    sourceNode,
    preferredOrigin,
    cols = GRID_COLS,
    itemWidth = NODE_W,
    itemHeight = NODE_H,
    excludedRects = [],
  } = opts;
  if (count <= 0) return [];

  // ── 1. 确定 Block 起点 ──
  let origin: Position;
  if (sourceNode) {
    origin = findNearestFreeSlot(sourceNode.position, count, existingNodes, cols, itemWidth, itemHeight, excludedRects);
  } else if (preferredOrigin) {
    origin = findNearestFreeSlot(preferredOrigin, count, existingNodes, cols, itemWidth, itemHeight, excludedRects);
  } else {
    origin = findOpenArea(existingNodes, excludedRects);
  }

  // ── 2. 计算 Block 包围盒尺寸 ──
  const actualCols = Math.min(cols, count);
  const rows = Math.ceil(count / actualCols);

  // ── 3. 按宫格排列各节点位置 ──
  const positions: Position[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % actualCols;
    const row = Math.floor(i / actualCols);
    positions.push({
      x: origin.x + col * (itemWidth + CELL_GAP),
      y: origin.y + row * (itemHeight + CELL_GAP),
    });
  }

  return positions;
}

export function getNextGroupedNodePosition(opts: GroupedPlacementOptions): Position {
  const {
    existingNodes,
    siblingNodes,
    sourceNode,
    preferredOrigin,
    cols = GRID_COLS,
    itemWidth = NODE_W,
    itemHeight = NODE_H,
    excludedRects = [],
  } = opts;
  const positionedSiblings = siblingNodes.filter((node) => node.position);

  if (positionedSiblings.length === 0) {
    return getGridBlockPositions({
      count: 1,
      existingNodes,
      sourceNode,
      preferredOrigin,
      cols,
      itemWidth,
      itemHeight,
      excludedRects,
    })[0] || DEFAULT_ORIGIN;
  }

  const actualCols = Math.max(1, cols);
  const anchor = positionedSiblings
    .slice()
    .sort((left, right) => left.position.y - right.position.y || left.position.x - right.position.x)[0]
    ?.position || positionedSiblings[0].position;
  const maxSlots = Math.max(positionedSiblings.length + actualCols * 4, actualCols * 2);

  for (let index = 0; index < maxSlots; index += 1) {
    const col = index % actualCols;
    const row = Math.floor(index / actualCols);
    const candidate = {
      x: anchor.x + col * (itemWidth + CELL_GAP),
      y: anchor.y + row * (itemHeight + CELL_GAP),
    };

    if (!blockCollides(candidate, itemWidth, itemHeight, existingNodes, excludedRects)) {
      return candidate;
    }
  }

  return getGridBlockPositions({
    count: 1,
    existingNodes,
    sourceNode,
    preferredOrigin,
    cols,
    itemWidth,
    itemHeight,
    excludedRects,
  })[0] || DEFAULT_ORIGIN;
}

// ══════════════════════════════════════════
// 向后兼容 API（内部委托 getGridBlockPositions）
// ══════════════════════════════════════════

/** @deprecated 使用 getGridBlockPositions */
export function getNextNodePosition(opts: PlacementOptions): Position {
  const positions = getGridBlockPositions({
    count: 1,
    existingNodes: opts.existingNodes,
    sourceNode: opts.sourceNode || null,
    preferredOrigin: opts.preferredOrigin || null,
    itemWidth: opts.itemWidth,
    itemHeight: opts.itemHeight,
    excludedRects: opts.excludedRects,
  });
  return positions[0] || DEFAULT_ORIGIN;
}

/** @deprecated 使用 getGridBlockPositions */
export function getTreeLayoutPositions(
  childCount: number,
  existingNodes: NodeLike[],
  sourcePosition?: Position,
): { source: Position; children: Position[] } {
  const sourcePos = sourcePosition || findOpenArea(existingNodes);
  const children = getGridBlockPositions({
    count: childCount,
    existingNodes,
    sourceNode: { position: sourcePos },
  });
  return { source: sourcePos, children };
}

// ══════════════════════════════════════════
// 内部工具
// ══════════════════════════════════════════

/** 在现有节点分布的下方找到空闲区域 */
function findOpenArea(existingNodes: NodeLike[], excludedRects: PlacementExclusionRect[] = []): Position {
  const occupiedNodes: NodeLike[] = [
    ...existingNodes,
    ...excludedRects.map((rect) => ({
      position: { x: rect.x, y: rect.y },
      width: rect.width,
      height: rect.height,
    })),
  ];
  if (occupiedNodes.length === 0) return DEFAULT_ORIGIN;

  let maxY = -Infinity;
  let minX = Infinity;
  for (const n of occupiedNodes) {
    const nodeHeight = getNodeLikeHeight(n);
    if (n.position.y + nodeHeight > maxY) maxY = n.position.y + nodeHeight;
    if (n.position.x < minX) minX = n.position.x;
  }

  // 放在所有节点下方（保持左对齐）
  return {
    x: Math.max(minX, DEFAULT_ORIGIN.x),
    y: maxY + BLOCK_GAP,
  };
}

/**
 * Block 级碰撞检测：检查一个 block 包围盒是否与已有节点重叠
 */
function blockCollides(
  origin: Position,
  blockW: number,
  blockH: number,
  existingNodes: NodeLike[],
  excludedRects: PlacementExclusionRect[] = [],
): boolean {
  const margin = 10;
  for (const node of existingNodes) {
    const nodeRight = node.position.x + getNodeLikeWidth(node);
    const nodeBottom = node.position.y + getNodeLikeHeight(node);
    const blockRight = origin.x + blockW;
    const blockBottom = origin.y + blockH;
    if (
      origin.x < nodeRight + margin &&
      blockRight > node.position.x - margin &&
      origin.y < nodeBottom + margin &&
      blockBottom > node.position.y - margin
    ) {
      return true;
    }
  }
  for (const rect of excludedRects) {
    const rectRight = rect.x + rect.width;
    const rectBottom = rect.y + rect.height;
    const blockRight = origin.x + blockW;
    const blockBottom = origin.y + blockH;
    if (
      origin.x < rectRight + margin
      && blockRight > rect.x - margin
      && origin.y < rectBottom + margin
      && blockBottom > rect.y - margin
    ) {
      return true;
    }
  }
  return false;
}

function getNodeLikeWidth(node: NodeLike): number {
  return Number.isFinite(node.width) && (node.width as number) > 0
    ? (node.width as number)
    : NODE_W;
}

function getNodeLikeHeight(node: NodeLike): number {
  return Number.isFinite(node.height) && (node.height as number) > 0
    ? (node.height as number)
    : NODE_H;
}

/**
 * 从源节点出发，向右→下→左→上四个方向逐步扩展搜索最近的空位。
 * 核心思想：以源节点为圆心的"螺旋扫描"，按欧氏距离排序候选位置，
 * 返回最近的无碰撞位置。
 */
function findNearestFreeSlot(
  sourcePos: Position,
  count: number,
  existingNodes: NodeLike[],
  cols: number = GRID_COLS,
  itemWidth: number = NODE_W,
  itemHeight: number = NODE_H,
  excludedRects: PlacementExclusionRect[] = [],
): Position {
  const actualCols = Math.min(cols, count);
  const rows = Math.ceil(count / actualCols);
  const blockW = actualCols * itemWidth + (actualCols - 1) * CELL_GAP;
  const blockH = rows * itemHeight + (rows - 1) * CELL_GAP;

  // 四个方向的候选起点偏移量 (dx, dy)
  const stepX = itemWidth + CELL_GAP;
  const stepY = itemHeight + CELL_GAP;

  // 生成按距离排序的候选位置：先尝试紧邻的 4 方向，再逐层扩展
  type Candidate = { x: number; y: number; dist: number };
  const candidates: Candidate[] = [];

  // 搜索半径（grid 步数）
  const maxSteps = 10;
  for (let dx = -maxSteps; dx <= maxSteps; dx++) {
    for (let dy = -maxSteps; dy <= maxSteps; dy++) {
      if (dx === 0 && dy === 0) continue; // 跳过源节点自身位置
      const cx = sourcePos.x + dx * stepX;
      const cy = sourcePos.y + dy * stepY;
      const dist = Math.abs(dx) + Math.abs(dy); // 曼哈顿距离（grid 步数）
      candidates.push({ x: cx, y: cy, dist });
    }
  }

  // 按曼哈顿距离排序，距离相同优先右→下
  candidates.sort((a, b) => {
    if (a.dist !== b.dist) return a.dist - b.dist;
    // 同距离：优先右侧（dx>0），再下方（dy>0）
    const aRight = a.x > sourcePos.x ? 0 : 1;
    const bRight = b.x > sourcePos.x ? 0 : 1;
    if (aRight !== bRight) return aRight - bRight;
    return a.y - b.y;
  });

  // 逐候选位置检测碰撞
  for (const c of candidates) {
    if (!blockCollides(c, blockW, blockH, existingNodes, excludedRects)) {
      return { x: c.x, y: c.y };
    }
  }

  // 兜底：所有候选都碰撞（极端情况），放在所有节点下方
  return findOpenArea(existingNodes, excludedRects);
}

/**
 * 碰撞检测 + 解决：如果候选位置与已有节点重叠（分轴 AABB），
 * 按 step 方向移动直到找到空位，最多尝试 30 次
 */
function resolveCollision(
  candidate: Position,
  existingNodes: NodeLike[],
  step: 'right' | 'down' = 'down',
): Position {
  let pos = { ...candidate };

  for (let attempt = 0; attempt < 30; attempt++) {
    const hasCollision = existingNodes.some(
      (n) => Math.abs(n.position.x - pos.x) < COLLISION_RADIUS_X
        && Math.abs(n.position.y - pos.y) < COLLISION_RADIUS_Y,
    );
    if (!hasCollision) return pos;
    pos = step === 'right'
      ? { x: pos.x + GAP_X, y: pos.y }
      : { x: pos.x, y: pos.y + GAP_Y };
  }

  return pos;
}
